import { GamingControls } from '../../gaming/GamingControls';
import { useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { useStore } from 'zustand';
import { cleanPortalUrl } from '../../core/channel';
import { nativeToolsStore } from '../../nativeTools/toolsPresence';
import { standaloneStudioUrl, type ConnectFragment } from '../../net/connect-link';
import { Icon } from '../components/Icon';
import { MenuPopup } from '../components/Menu';
import { useActions, useProject, useRuntime, useUi, useUiContext } from '../context';
import { useCollections } from '../dialogs/misc/CollectionsDialog';
import { shortcutLabel, useHotkeyRegistry } from '../hotkeys/HotkeyManager';
import { buildMenu, MENU_TITLES, type MenuTitle } from './menus';
import { logoFor } from '../theme/theme';
import { selectTheme } from '../uiStore';
import './shell.css';

/** Top menu bar. Menus are built when opened, so they always show current state. */
export function MenuBar({ connect }: { connect?: ConnectFragment }): JSX.Element {
  const { studio, ui } = useUiContext();
  const actions = useActions();
  const registry = useHotkeyRegistry();
  const collectionName = useRuntime((r) => r.collection.name);
  const projectName = useProject((p) => p.name);
  const portalUrl = useProject((p) => p.channel.portalUrl);
  const embedded = useUi((s) => s.embedded);
  const toolsConnected = useStore(nativeToolsStore, (s) => s.connected);
  const { list: collections } = useCollections();
  // Re-render an open menu when the dock set changes (checked states in View > Docks).
  useUi((s) => s.dockRevision);
  const theme = useUi(selectTheme);
  const [open, setOpen] = useState<{ title: MenuTitle; x: number; y: number } | null>(null);
  const buttons = useRef(new Map<MenuTitle, HTMLButtonElement>());

  const show = (title: MenuTitle): void => {
    const button = buttons.current.get(title);
    if (!button) return;
    const rect = button.getBoundingClientRect();
    ui.getState().closeMenu();
    setOpen({ title, x: rect.left, y: rect.bottom + 1 });
  };

  const close = (): void => {
    const title = open?.title;
    setOpen(null);
    if (title) buttons.current.get(title)?.focus();
  };

  const step = (from: MenuTitle, direction: 1 | -1): MenuTitle =>
    MENU_TITLES[(MENU_TITLES.indexOf(from) + direction + MENU_TITLES.length) % MENU_TITLES.length]!;

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, title: MenuTitle): void => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      buttons.current.get(step(title, event.key === 'ArrowRight' ? 1 : -1))?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      show(title);
    }
  };

  const stored = studio.project.getState().project.hotkeys;
  const items = open
    ? buildMenu(open.title, { studio, ui, actions, collections, shortcut: (id) => shortcutLabel(registry, stored, id) })
    : [];

  return (
    <nav className="menubar" role="menubar" aria-label="Main menu">
      <img className="menubar-logo" src={logoFor(theme)} alt="Perck" title="Perck Studio" draggable={false} />
      {MENU_TITLES.map((title, index) => (
        <button
          key={title}
          ref={(node) => {
            if (node) buttons.current.set(title, node);
            else buttons.current.delete(title);
          }}
          type="button"
          role="menuitem"
          className="menubar-item"
          data-menu-anchor=""
          aria-haspopup="menu"
          aria-expanded={open?.title === title}
          tabIndex={index === 0 ? 0 : -1}
          onClick={() => (open?.title === title ? close() : show(title))}
          onPointerEnter={() => open && open.title !== title && show(title)}
          onKeyDown={(e) => onKeyDown(e, title)}
        >
          {title}
        </button>
      ))}
      {import.meta.env.VITE_GAMING_STUDIO_ENABLED === 'true' && <GamingControls controller={studio.gaming} />}
      {embedded ? <div className="menubar-actions">
        {toolsConnected !== true && <a
          className="menubar-action" href={`${cleanPortalUrl(connect?.link?.portalUrl ?? portalUrl)}/downloads`}
          target="_blank" rel="noopener noreferrer" aria-label="Download omp-tools" title="Download omp-tools"
        ><Icon name="down" size={14} /><span className="menubar-download-label">Download omp-tools</span></a>}
        <a className="menubar-action" href={standaloneStudioUrl(window.location.href, connect?.link ?? null)}
          target="_blank" rel="noopener noreferrer" title="Open the standalone studio in a new tab (advanced)"
        >Standalone<span className="menubar-advanced-label"> (advanced)</span><Icon name="external" size={12} /></a>
      </div> : <span className="menubar-title ui-ellipsis" title="Open scene collection">{projectName || collectionName}</span>}
      {open ? (
        <MenuPopup
          key={open.title}
          items={items}
          x={open.x}
          y={open.y}
          label={open.title}
          onClose={close}
          onNavigate={(direction) => show(step(open.title, direction))}
        />
      ) : null}
    </nav>
  );
}
